/**
 * Metis Owner Portal — curated view (V1 deck S17 "two audiences, one truth").
 *
 * Reads the same allocated_mwh table the asset-manager views read; lives
 * outside the core component tree so Metis branding decisions can't leak
 * into vanilla portfolios.
 *
 * URL: /clients/metis/owner-portal
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Stack, Chip, Alert,
  FormControl, Select, MenuItem, CircularProgress,
} from '@mui/material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// Metis brand tokens — kept inline so the curated portal can never drift
// out of sync with the rest of Lumeros's design tokens.
const METIS_INK    = '#0a1626';
const METIS_AMBER  = '#D98A2B';
const METIS_PAPER  = '#FAFAF7';

const PERIODS = [
  { code: 'mtd', label: 'Month-to-date' },
  { code: '7d',  label: 'Last 7 days' },
  { code: '30d', label: 'Last 30 days' },
  { code: '90d', label: 'Last 90 days' },
  { code: 'ytd', label: 'Year-to-date' },
];


const fmtAud = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return `A$${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `A$${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `A$${(v / 1e3).toFixed(0)}k`;
  return `A$${v.toFixed(0)}`;
};
const fmtMwh = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString('en-AU')}`);


function HeroKpi({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{
      flex: '1 1 200px', minWidth: 200, borderColor: 'rgba(10,22,38,0.12)',
      bgcolor: METIS_PAPER, borderRadius: 2,
    }}>
      <CardContent sx={{ py: 2 }}>
        <Typography sx={{
          fontSize: '0.7rem', fontWeight: 700, color: 'rgba(10,22,38,0.5)',
          textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '1.7rem', fontWeight: 700, lineHeight: 1.1,
          letterSpacing: '-0.02em', color: METIS_INK,
        }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(10,22,38,0.6)', mt: 0.5 }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}


function RevenueSliceBar({ k }) {
  // Show only non-zero slices.
  const slices = [
    { key: 'ppa_aud',      label: 'PPA',      color: METIS_AMBER },
    { key: 'merchant_aud', label: 'Merchant', color: '#E9B065' },
    { key: 'cap_aud',      label: 'Cap',      color: '#3b82f6' },
    { key: 'fcas_aud',     label: 'FCAS',     color: '#f59e0b' },
  ].map((s) => ({ ...s, aud: k[s.key] || 0 }))
   .filter((s) => Math.abs(s.aud) > 0);

  const total = slices.reduce((a, s) => a + s.aud, 0);
  if (!total) return null;

  return (
    <Card variant="outlined" sx={{ borderColor: 'rgba(10,22,38,0.12)', borderRadius: 2 }}>
      <CardContent>
        <Typography sx={{
          fontSize: '0.7rem', fontWeight: 700, color: 'rgba(10,22,38,0.5)',
          textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.25,
        }}>
          Revenue composition
        </Typography>
        <Box sx={{
          display: 'flex', height: 14, width: '100%',
          borderRadius: 1.5, overflow: 'hidden', mb: 1.25,
          bgcolor: 'rgba(10,22,38,0.04)',
        }}>
          {slices.map((s) => {
            const pct = total ? (s.aud / total * 100) : 0;
            if (pct <= 0) return null;
            return <Box key={s.key} sx={{ width: `${pct}%`, bgcolor: s.color }} />;
          })}
        </Box>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {slices.map((s) => (
            <Stack key={s.key} direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: s.color }} />
              <Typography sx={{ fontSize: '0.78rem', color: METIS_INK }}>
                {s.label}: <b>{fmtAud(s.aud)}</b>
                <Box component="span" sx={{ color: 'rgba(10,22,38,0.5)', ml: 0.5 }}>
                  ({((s.aud / total) * 100).toFixed(0)}%)
                </Box>
              </Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


export default function MetisOwnerPortal() {
  const [period, setPeriod] = useState('30d');
  const [data, setData]     = useState(null);
  const [err, setErr]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios.get(`${API_BASE_URL}/api/clients/metis/owner-portal`, { params: { period } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  // Brand-scoped wrapper — keeps Metis colours from leaking into the global
  // theme. Light background, ink heading, amber accent on the period chip.
  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: METIS_PAPER, color: METIS_INK,
      px: { xs: 2, sm: 4, md: 6 }, py: { xs: 3, sm: 4 },
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
              sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography sx={{
            fontSize: '1.85rem', fontWeight: 700, letterSpacing: '-0.025em',
            color: METIS_INK,
          }}>
            Metis Owner Portal
          </Typography>
          {data?.portfolio?.name && (
            <Chip label={data.portfolio.name} size="small"
                  sx={{ bgcolor: METIS_AMBER, color: '#fff',
                        fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
          )}
        </Stack>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIODS.map((p) => (
              <MenuItem key={p.code} value={p.code}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Typography sx={{ color: 'rgba(10,22,38,0.6)', fontSize: '0.9rem', mb: 3 }}>
        ●  {data?.period?.label || PERIODS.find((p) => p.code === period)?.label}
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {loading && (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} sx={{ color: METIS_AMBER }} />
        </Box>
      )}

      {!loading && data?.available === false && (
        <Card sx={{ p: 4, textAlign: 'center', borderColor: 'rgba(10,22,38,0.15)' }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 1 }}>
            Portal not configured
          </Typography>
          <Typography sx={{ color: 'rgba(10,22,38,0.6)' }}>
            {data?.reason || 'Awaiting Metis portfolio setup.'}
          </Typography>
        </Card>
      )}

      {!loading && data?.available !== false && data?.kpis && (
        <>
          {/* Headline KPI strip — generation, revenue, asset count. */}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
            <HeroKpi
              label="Generation"
              value={`${fmtMwh(data.kpis.generation_mwh)} MWh`}
              sub={`${data.kpis.asset_count} asset${data.kpis.asset_count === 1 ? '' : 's'}`}
            />
            <HeroKpi
              label="Revenue"
              value={fmtAud(data.kpis.revenue_aud)}
              sub="settlement basis, no LGC value"
            />
            <HeroKpi
              label="PPA share"
              value={data.kpis.revenue_aud
                ? `${((data.kpis.ppa_aud / data.kpis.revenue_aud) * 100).toFixed(0)}%`
                : '—'}
              sub={fmtAud(data.kpis.ppa_aud)}
            />
            <HeroKpi
              label="Merchant share"
              value={data.kpis.revenue_aud
                ? `${((data.kpis.merchant_aud / data.kpis.revenue_aud) * 100).toFixed(0)}%`
                : '—'}
              sub={fmtAud(data.kpis.merchant_aud)}
            />
          </Stack>

          <RevenueSliceBar k={data.kpis} />

          <Typography sx={{
            mt: 3, fontSize: '0.75rem', color: 'rgba(10,22,38,0.5)',
          }}>
            {data.source || 'Source: aus_warehouse — same truth as the asset-manager view.'}
          </Typography>
        </>
      )}
    </Box>
  );
}
